import { useCallback, useEffect, useRef, useState } from 'react';

const VISIBLE_ITEMS_LIMIT = 10;

type useLazyLoadProps = {
  itemCount: number;
  initialVisibleItems?: number;
  increment?: number;
};

const useLazyLoad = ({
  itemCount,
  initialVisibleItems = VISIBLE_ITEMS_LIMIT,
  increment = VISIBLE_ITEMS_LIMIT,
}: useLazyLoadProps) => {
  const [visibleItems, setVisibleItems] = useState<number>(initialVisibleItems);
  const observer = useRef<IntersectionObserver | null>(null);

  const loadMoreItems = useCallback(() => {
    setVisibleItems((current) => Math.min(current + increment, itemCount));
  }, [increment, itemCount]);

  const setObserverTarget = useCallback(
    (target: Element | null) => {
      if (observer.current) {
        observer.current.disconnect();
      }

      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            loadMoreItems();
          }
        },
        { threshold: 0.3 }
      );

      if (target) {
        observer.current.observe(target);
      }
    },
    [loadMoreItems]
  );

  useEffect(
    () => () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    },
    []
  );

  return { visibleItems, loadMoreItems, setObserverTarget };
};

export default useLazyLoad;
